type ResolveConcurrency = () => number;

const normalizePoolSize = (resolveConcurrency: ResolveConcurrency): number =>
  Math.max(1, Math.floor(resolveConcurrency()));

export const createConcurrencyPool = (
  resolveConcurrency: ResolveConcurrency
) => {
  let activeCount = 0;
  const queue: Array<() => void> = [];

  const tryDequeue = () => {
    const poolSize = normalizePoolSize(resolveConcurrency);

    while (activeCount < poolSize && queue.length > 0) {
      activeCount += 1;
      const next = queue.shift();

      next?.();
    }
  };

  const run = <T>(task: () => Promise<T>): Promise<T> =>
    new Promise((resolve, reject) => {
      queue.push(() => {
        task()
          .then(resolve, reject)
          .finally(() => {
            activeCount -= 1;
            tryDequeue();
          });
      });

      tryDequeue();
    });

  return { run };
};

export const runWithConcurrencyPool = async <T, R>(
  items: readonly T[],
  resolveConcurrency: ResolveConcurrency,
  worker: (item: T, index: number) => Promise<R>
): Promise<R[]> => {
  if (items.length === 0) return [];

  const poolSize = Math.min(
    normalizePoolSize(resolveConcurrency),
    items.length
  );
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const runNext = async (): Promise<void> => {
    while (true) {
      const index = nextIndex;
      nextIndex += 1;

      if (index >= items.length) return;

      results[index] = await worker(items[index]!, index);
    }
  };

  await Promise.all(Array.from({ length: poolSize }, () => runNext()));

  return results;
};
